import { type ReactNode } from 'react';
import { EmptyState, SegmentedControl } from '@/shared/components';
import type { PropertyFileTreeNode, TrashedFileResponse } from '@/shared/api';
import { AkteBrowserToolbar } from './AkteBrowserToolbar';
import { AkteContentList } from './AkteContentList';
import { AkteFachansicht, hasFachansicht, type FachansichtCallbacks } from './AkteFachansicht';
import { AkteTrashView } from './AkteTrashView';
import { collectFilesDeep, findNodeByPath } from './akteBrowserNav';
import { categoryOf } from './akteFolderRules';
import type { FileActions } from './akteFileActions';
import type { useAkteBrowser } from './useAkteBrowser';
import type { useAkteDragMove } from './useAkteDragMove';
import type { AkteBulkHandlers } from './useAkteBulkDialogs';

interface Props {
  propertyId: string;
  tree: PropertyFileTreeNode;
  trash: { data: TrashedFileResponse[] | undefined; isLoading: boolean };
  browser: ReturnType<typeof useAkteBrowser>;
  showTrash: boolean;
  actions: FileActions;
  bulk: AkteBulkHandlers;
  folderMenu: (node: PropertyFileTreeNode) => ReactNode;
  onUploadInto: (path: string) => void;
  drag: ReturnType<typeof useAkteDragMove>;
  fachansicht: FachansichtCallbacks;
}

/** Die Arbeitsfläche der Akte: Papierkorb oder Werkzeugleiste plus Ordnerinhalt. */
export function AkteBrowserMain({
  propertyId,
  tree,
  trash,
  browser,
  showTrash,
  actions,
  bulk,
  folderMenu,
  onUploadInto,
  drag,
  fachansicht,
}: Props) {
  const currentNode = findNodeByPath(tree, browser.folderPath);
  const category = categoryOf(browser.folderPath);
  const showFachToggle = !showTrash && !!currentNode && hasFachansicht(category);
  const ausgehend = findNodeByPath(tree, '03_Rechnungen/Ausgehend');

  if (showTrash) {
    return (
      <AkteTrashView propertyId={propertyId} items={trash.data ?? []} loading={trash.isLoading} />
    );
  }
  if (!currentNode) {
    return <EmptyState icon="Error" title="Ordner nicht gefunden" />;
  }

  return (
    <>
      <AkteBrowserToolbar
        path={browser.folderPath}
        search={browser.search}
        viewMode={browser.viewMode}
        onNavigate={browser.navigateTo}
        onSearch={browser.setSearch}
        onViewMode={browser.setViewMode}
        onUploadHere={() => onUploadInto(browser.folderPath)}
      />
      {showFachToggle && (
        <div className="mb-3">
          <SegmentedControl
            value={browser.fachansicht ? 'fach' : 'docs'}
            onChange={(v) => browser.setFachansicht(v === 'fach')}
            options={[
              { value: 'docs', label: 'Dokumente' },
              { value: 'fach', label: 'Fachansicht' },
            ]}
          />
        </div>
      )}
      {showFachToggle && browser.fachansicht ? (
        <AkteFachansicht
          propertyId={propertyId}
          category={category!}
          ausgehendFiles={ausgehend ? collectFilesDeep(ausgehend) : []}
          onUploadHere={() => onUploadInto(browser.folderPath)}
          {...fachansicht}
        />
      ) : (
        <AkteContentList
          node={currentNode}
          propertyId={propertyId}
          viewMode={browser.viewMode}
          search={browser.search}
          selection={browser.selection}
          actions={actions}
          onNavigate={browser.navigateTo}
          onUploadHere={() => onUploadInto(browser.folderPath)}
          folderMenu={folderMenu}
          drag={drag}
          bulk={bulk}
        />
      )}
    </>
  );
}
